import React, { useState } from 'react';
import { strategistService, type ChatMessage } from '@/services/strategistService';

interface StrategistChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onPrescribeAction: (action: {
    title: string;
    category: string;
    lead_time: number;
    affiliates?: string[];
  }) => void;
}

export const StrategistChatDrawer: React.FC<StrategistChatDrawerProps> = ({
  isOpen,
  onClose,
  onPrescribeAction,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      sender: 'assistant',
      text: 'Olá! Sou o **Estrategista de Inteligência e Receita**.\n\nTrabalho sobre a base nacional de sazonalidade e calendário comercial. Como posso orientar o planejamento editorial e comercial da sua equipe hoje?',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSend = async (textToSend?: string) => {
    const query = textToSend || input;
    if (!query.trim() || loading) return;

    const userMsg: ChatMessage = {
      id: `u_${Date.now()}`,
      sender: 'user',
      text: query,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    setMessages((prev) => [...prev, userMsg]);
    if (!textToSend) setInput('');
    setLoading(true);

    try {
      const historyPayload = messages.map((m) => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text,
      }));

      const res = await strategistService.chat(query, historyPayload);
      const botMsg: ChatMessage = {
        id: `b_${Date.now()}`,
        sender: 'assistant',
        text: res.reply,
        suggestedAction: res.suggested_action,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, botMsg]);
    } catch {
      const errorMsg: ChatMessage = {
        id: `err_${Date.now()}`,
        sender: 'assistant',
        text: 'Erro ao conectar ao motor do Data Lake. Tente novamente em instantes.',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const quickPrompts = [
    'Quais as prioridades de Outubro?',
    'Como está o calendário de liquidez?',
    'Quando antecipar campanhas de Ar-Condicionado?',
    'Qual a rampa de ignição da Black Friday?',
  ];

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--bg-overlay)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 1200,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '560px',
          maxWidth: '92vw',
          height: '100%',
          backgroundColor: 'var(--bg-0)',
          borderLeft: '1px solid var(--border-1)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header do Chat */}
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid var(--border-1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'var(--bg-1)',
          }}
        >
          <div>
            <h2
              style={{
                fontSize: '16px',
                fontWeight: 800,
                margin: 0,
                color: 'var(--text-1)',
                letterSpacing: '-0.02em',
              }}
            >
              Copiloto Estrategista
            </h2>
            <div
              style={{
                fontSize: 'var(--fs-xs)',
                color: 'var(--sig-ok-text)',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '6px',
              }}
            >
              <div
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--sig-ok)',
                }}
              />
              Base nacional de sazonalidade (varejo e finanças)
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: 'var(--text-3)',
              fontSize: '18px',
              cursor: 'pointer',
              fontWeight: 800,
            }}
          >
            ✕
          </button>
        </div>

        {/* Corpo do Chat */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          {messages.map((m) => (
            <div
              key={m.id}
              style={{
                alignSelf: m.sender === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
              }}
            >
              <div
                style={{
                  fontSize: 'var(--fs-xs)',
                  color: 'var(--text-4)',
                  marginBottom: '6px',
                  textAlign: m.sender === 'user' ? 'right' : 'left',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {m.sender === 'user' ? 'Você' : 'Estrategista'} • {m.timestamp}
              </div>
              <div
                style={{
                  backgroundColor: m.sender === 'user' ? 'var(--nexus-brand)' : 'var(--bg-1)',
                  color: m.sender === 'user' ? 'var(--text-on-accent)' : 'var(--text-1)',
                  border: m.sender === 'user' ? 'none' : '1px solid var(--border-2)',
                  padding: '14px 18px',
                  borderRadius: 'var(--r-md)',
                  borderTopRightRadius: m.sender === 'user' ? '2px' : 'var(--r-md)',
                  borderTopLeftRadius: m.sender === 'assistant' ? '2px' : 'var(--r-md)',
                  fontSize: 'var(--fs-sm)',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  boxShadow: m.sender === 'user' ? '0 4px 12px rgba(37,99,235,0.2)' : 'none',
                }}
              >
                {/* Formatação Simples de Markdown para Negrito */}
                {m.text.split('**').map((part, index) =>
                  index % 2 === 1 ? (
                    <strong
                      key={index}
                      style={{
                        color: m.sender === 'user' ? '#FFFFFF' : 'var(--text-1)',
                        fontWeight: 800,
                      }}
                    >
                      {part}
                    </strong>
                  ) : (
                    part
                  ),
                )}
              </div>

              {/* Action Widget de Prescrição */}
              {m.suggestedAction && (
                <div
                  style={{
                    marginTop: '12px',
                    backgroundColor: 'var(--bg-2)',
                    border: '1px solid var(--border-2)',
                    borderRadius: 'var(--r-md)',
                    padding: '16px',
                    boxShadow: 'var(--shadow-1)',
                  }}
                >
                  <div
                    style={{
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--text-3)',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      marginBottom: '8px',
                      letterSpacing: '0.04em',
                    }}
                  >
                    Prescrição Recomendada
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--fs-sm)',
                      fontWeight: 700,
                      color: 'var(--text-1)',
                      marginBottom: '4px',
                    }}
                  >
                    {m.suggestedAction.title}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--text-2)',
                      marginBottom: '14px',
                      display: 'flex',
                      gap: '10px',
                    }}
                  >
                    <span style={{ color: 'var(--nexus-brand)', fontWeight: 600 }}>
                      {m.suggestedAction.category}
                    </span>
                    <span>Lead Time: {m.suggestedAction.lead_time} dias</span>
                  </div>
                  <button
                    onClick={() => {
                      if (m.suggestedAction) {
                        onPrescribeAction({
                          title: m.suggestedAction.title,
                          category: m.suggestedAction.category,
                          lead_time: m.suggestedAction.lead_time,
                          affiliates: m.suggestedAction.affiliates,
                        });
                      }
                    }}
                    style={{
                      width: '100%',
                      backgroundColor: 'var(--nexus-brand)',
                      color: 'var(--text-on-accent)',
                      border: 'none',
                      padding: '10px',
                      borderRadius: 'var(--r-sm)',
                      fontSize: 'var(--fs-xs)',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
                    }}
                  >
                    Lançar no Kanban Estratégico
                  </button>
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div
              style={{
                alignSelf: 'flex-start',
                color: 'var(--text-4)',
                fontSize: 'var(--fs-xs)',
                fontWeight: 600,
                padding: '10px 16px',
                backgroundColor: 'var(--bg-1)',
                borderRadius: '16px',
              }}
            >
              Processando correlações do Data Lake...
            </div>
          )}
        </div>

        {/* Input Area */}
        <div
          style={{
            padding: '24px',
            backgroundColor: 'var(--bg-1)',
            borderTop: '1px solid var(--border-1)',
          }}
        >
          {messages.length === 1 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
              {quickPrompts.map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSend(prompt)}
                  style={{
                    backgroundColor: 'var(--bg-2)',
                    border: '1px solid var(--border-2)',
                    color: 'var(--text-2)',
                    borderRadius: '20px',
                    padding: '8px 14px',
                    fontSize: 'var(--fs-xs)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--nexus-brand-bg)';
                    e.currentTarget.style.color = 'var(--nexus-brand)';
                    e.currentTarget.style.borderColor = 'var(--nexus-brand)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-2)';
                    e.currentTarget.style.color = 'var(--text-2)';
                    e.currentTarget.style.borderColor = 'var(--border-2)';
                  }}
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSend();
              }}
              placeholder="Pergunte sobre antecipação, nichos ou liquidez..."
              style={{
                flex: 1,
                backgroundColor: 'var(--bg-2)',
                border: '1px solid var(--border-2)',
                borderRadius: 'var(--r-md)',
                padding: '14px 16px',
                color: 'var(--text-1)',
                fontSize: 'var(--fs-sm)',
                outline: 'none',
              }}
            />
            <button
              onClick={() => handleSend()}
              disabled={loading || !input.trim()}
              style={{
                backgroundColor: 'var(--nexus-brand)',
                color: 'var(--text-on-accent)',
                border: 'none',
                borderRadius: 'var(--r-md)',
                padding: '0 24px',
                fontWeight: 700,
                fontSize: 'var(--fs-sm)',
                cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                opacity: loading || !input.trim() ? 0.5 : 1,
              }}
            >
              Enviar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
